import { CONVERSATION_MODES_STORAGE_KEY } from '@/sections/insights/aiAssistant/constants/keys';
import { MAX_STORED_MODES } from '@/sections/insights/aiAssistant/constants/limits';
import type {
  ConversationMode,
  PersistableConversationMode,
} from '@/sections/insights/aiAssistant/types/conversation.types';
import { getTenantScopedKey } from '@/utils/storage/tenantScoping.util';

export function getConversationModes(): Record<string, ConversationMode> {
  try {
    const scopedKey = getTenantScopedKey(CONVERSATION_MODES_STORAGE_KEY);
    const scopedSaved = localStorage.getItem(scopedKey);
    if (scopedSaved) {
      return JSON.parse(scopedSaved);
    }
    // Fall back to legacy bare key for backward compatibility during migration
    const legacySaved = localStorage.getItem(CONVERSATION_MODES_STORAGE_KEY);
    return legacySaved ? JSON.parse(legacySaved) : {};
  } catch {
    return {};
  }
}

export function saveConversationMode(
  convId: string,
  convMode: PersistableConversationMode,
  agentName?: string
): void {
  const modes = getConversationModes();
  modes[convId] = {
    mode: convMode,
    agent: agentName,
  };

  // Limit storage to prevent unbounded growth (keep most recent MAX_STORED_MODES)
  const keys = Object.keys(modes);
  if (keys.length > MAX_STORED_MODES) {
    keys.slice(0, keys.length - MAX_STORED_MODES).forEach(k => delete modes[k]);
  }

  const scopedKey = getTenantScopedKey(CONVERSATION_MODES_STORAGE_KEY);
  localStorage.setItem(scopedKey, JSON.stringify(modes));
}

export function getConversationMode(convId: string): ConversationMode {
  const modes = getConversationModes();
  return modes[convId] || { mode: 'query' };
}

/**
 * Resolve a persisted mode into a selectable chat mode. A selected/restored
 * conversation is always a chat conversation, so a stale 'auto' (hunt) heals to
 * 'query'. Optionally downgrades 'agent'->'query' when agent mode is unavailable.
 */
export function resolveSelectableMode(
  saved: ConversationMode,
  opts?: { canUseAgentMode?: boolean }
): PersistableConversationMode {
  if (saved.mode === 'auto') return 'query';
  if (saved.mode === 'agent' && opts?.canUseAgentMode === false) return 'query';
  return saved.mode;
}
